import {
  IAvailabilityOp,
  ICategoryCreate,
  ICategoryPatch,
} from '@/interfaces/ICatalogV2';
import { ICategory } from '@/interfaces/ICategory';
import { ISimpleHourRange } from '@/interfaces/ISimpleHourRange';
import {
  formChangedKeys,
  formRebaseAfterSave,
  formRebaseOnRemote,
  IFormModel,
  IRemoteRebase,
  sameFormKey,
} from './form-model';

/**
 * The category editor's form (plan §5.15). «¿Dónde se ve?» is one of three
 * cards mapped to `{ visible, posOnly }`; «Oculta» keeps the stored
 * `posOnly`, so showing it again restores where it was sold.
 */
export type TCategoryWhere = 'everywhere' | 'posOnly' | 'hidden';

export interface ICategoryForm {
  name: string;
  description: string;
  where: TCategoryWhere;
  /** The stored `posOnly`, kept while the category is hidden. */
  posOnly: boolean;
  hiddenBranchIds: number[];
  hourRanges: ISimpleHourRange[] | null;
  /** `[]` = never prints. */
  comandaPrinters: number[];
  reportingCategoryId: number | null;
  /** Its products are kept out of the storefront's «Lo más pedido» (owner 2026-11-03). */
  popularHidden: boolean;
}

export type TCategoryFormKey = keyof ICategoryForm;

export const CATEGORY_FORM_KEYS: readonly TCategoryFormKey[] = [
  'name',
  'description',
  'where',
  'posOnly',
  'hiddenBranchIds',
  'hourRanges',
  'comandaPrinters',
  'reportingCategoryId',
  'popularHidden',
];

export const MAX_CATEGORY_NAME = 128;
export const MAX_CATEGORY_DESCRIPTION = 255;

function trimmedOrNull(text: string): string | null {
  const trimmed = text.trim();
  return trimmed ? trimmed : null;
}

export function categoryWhere(
  category: Pick<ICategory, 'visible' | 'posOnly'>
): TCategoryWhere {
  if (!category.visible) return 'hidden';
  return category.posOnly ? 'posOnly' : 'everywhere';
}

export function categoryFormFromCategory(category: ICategory): ICategoryForm {
  return {
    name: category.name ?? '',
    description: category.description ?? '',
    where: categoryWhere(category),
    posOnly: !!category.posOnly,
    hiddenBranchIds: (category.hiddenBranchesIds ?? [])
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id))
      .sort((a, b) => a - b),
    hourRanges: category.hourRanges?.length
      ? category.hourRanges.map((range) => ({ ...range }))
      : null,
    comandaPrinters: [...(category.comandaPrinters ?? [])],
    reportingCategoryId: category.reportingCategoryId ?? null,
    popularHidden: !!category.popularHidden,
  };
}

/**
 * The create form. The default printer is always shown selected, never
 * applied silently: station 1 when it exists, else the lowest one.
 */
export function emptyCategoryForm(
  stationIds: readonly number[]
): ICategoryForm {
  const printer = stationIds.includes(1)
    ? 1
    : stationIds.length
    ? Math.min(...stationIds)
    : null;
  return {
    name: '',
    description: '',
    where: 'everywhere',
    posOnly: false,
    hiddenBranchIds: [],
    hourRanges: null,
    comandaPrinters: printer === null ? [] : [printer],
    reportingCategoryId: null,
    popularHidden: false,
  };
}

function keyValue(form: ICategoryForm, key: TCategoryFormKey): unknown {
  switch (key) {
    case 'name':
      return form.name.trim();
    case 'description':
      return trimmedOrNull(form.description);
    case 'hiddenBranchIds':
      return [...form.hiddenBranchIds].sort((a, b) => a - b);
    case 'comandaPrinters':
      return [...form.comandaPrinters].sort((a, b) => a - b);
    case 'hourRanges':
      return form.hourRanges?.length ? form.hourRanges : null;
    default:
      return form[key];
  }
}

const CATEGORY_FORM_MODEL: IFormModel<ICategoryForm> = {
  keys: CATEGORY_FORM_KEYS,
  value: keyValue,
};

export function sameCategoryKey(
  a: ICategoryForm,
  b: ICategoryForm,
  key: TCategoryFormKey
): boolean {
  return sameFormKey(CATEGORY_FORM_MODEL, a, b, key);
}

export function changedCategoryKeys(
  a: ICategoryForm,
  b: ICategoryForm
): TCategoryFormKey[] {
  return formChangedKeys(CATEGORY_FORM_MODEL, a, b);
}

export function rebaseCategoryAfterSave(
  form: ICategoryForm,
  sent: ICategoryForm,
  saved: ICategoryForm
): ICategoryForm {
  return formRebaseAfterSave(CATEGORY_FORM_MODEL, form, sent, saved);
}

export function rebaseCategoryOnRemote(
  base: ICategoryForm,
  form: ICategoryForm,
  remote: ICategoryForm
): IRemoteRebase<ICategoryForm> {
  return formRebaseOnRemote(CATEGORY_FORM_MODEL, base, form, remote);
}

export interface ICategoryFormErrors {
  name?: 'required' | 'tooLong';
  description?: 'tooLong';
}

export function validateCategoryForm(form: ICategoryForm): ICategoryFormErrors {
  const errors: ICategoryFormErrors = {};
  const name = form.name.trim();
  if (!name) errors.name = 'required';
  else if (name.length > MAX_CATEGORY_NAME) errors.name = 'tooLong';
  if (form.description.trim().length > MAX_CATEGORY_DESCRIPTION)
    errors.description = 'tooLong';
  return errors;
}

/** `{ visible, posOnly }` of a «¿Dónde se ve?» choice. */
function placement(
  form: ICategoryForm
): { visible: boolean; posOnly: boolean } {
  if (form.where === 'hidden') return { visible: false, posOnly: form.posOnly };
  return { visible: true, posOnly: form.where === 'posOnly' };
}

function availabilityOp(
  base: ICategoryForm,
  form: ICategoryForm
): IAvailabilityOp | null {
  const before = placement(base);
  const after = placement(form);
  const hidden = new Set(form.hiddenBranchIds);
  const baseHidden = new Set(base.hiddenBranchIds);
  const hideInBranchIds = form.hiddenBranchIds.filter(
    (id) => !baseHidden.has(id)
  );
  const showInBranchIds = base.hiddenBranchIds.filter((id) => !hidden.has(id));
  const op: IAvailabilityOp = {
    ...(before.visible !== after.visible ? { visible: after.visible } : {}),
    ...(hideInBranchIds.length ? { hideInBranchIds } : {}),
    ...(showInBranchIds.length ? { showInBranchIds } : {}),
  };
  return Object.keys(op).length ? op : null;
}

/** The `PATCH /v2/categories/:id` body of the changed keys; null when nothing changed. */
export function diffCategoryForm(
  base: ICategoryForm,
  form: ICategoryForm
): ICategoryPatch | null {
  const keys = changedCategoryKeys(base, form);
  if (!keys.length) return null;
  const patch: ICategoryPatch = {};

  if (keys.includes('name')) patch.name = form.name.trim();
  if (keys.includes('description'))
    patch.description = trimmedOrNull(form.description);
  const posOnly = placement(form).posOnly;
  if (posOnly !== placement(base).posOnly) patch.posOnly = posOnly;
  const availability = availabilityOp(base, form);
  if (availability) patch.availability = availability;
  if (keys.includes('hourRanges'))
    patch.hourRanges = form.hourRanges?.length ? form.hourRanges : null;
  if (keys.includes('comandaPrinters'))
    patch.comandaPrinters = [...form.comandaPrinters];
  if (keys.includes('reportingCategoryId'))
    patch.reportingCategoryId = form.reportingCategoryId;
  if (keys.includes('popularHidden')) patch.popularHidden = form.popularHidden;

  return Object.keys(patch).length ? patch : null;
}

/** The `POST /v2/categories` body. */
export function createCategoryPayload(
  form: ICategoryForm,
  restaurantId: number,
  menuId: number | null
): ICategoryCreate {
  const { visible, posOnly } = placement(form);
  const payload: ICategoryCreate = {
    restaurantId,
    menuId,
    name: form.name.trim(),
    posOnly,
    comandaPrinters: [...form.comandaPrinters],
  };
  const description = trimmedOrNull(form.description);
  if (description) payload.description = description;
  if (form.hourRanges?.length) payload.hourRanges = form.hourRanges;
  if (form.reportingCategoryId !== null)
    payload.reportingCategoryId = form.reportingCategoryId;
  const op: IAvailabilityOp = {
    ...(!visible ? { visible: false } : {}),
    ...(form.hiddenBranchIds.length
      ? { hiddenBranchIds: [...form.hiddenBranchIds] }
      : {}),
  };
  if (Object.keys(op).length) payload.availability = op;
  return payload;
}
